import type { PmtilesTileType, RasterType, TilePixels, TileReader } from "@pax/basemap";
import type { DecodeRequest, DecodeResponse } from "./tile-decoder.worker";

/** A few workers that decode tiles, so a world loads, and a package checks, without stalling the page. */
export interface TileDecoder {
  /** How many workers decode at once. */
  readonly workers: number;
  decode: (
    type: RasterType,
    bytes: ArrayBuffer,
    encoding: "blob" | PmtilesTileType,
  ) => Promise<TilePixels>;
  /** `readTileForCheck`, in a worker: `checkPackage`'s `readTile`. */
  check: TileReader;
  dispose: () => void;
}

type Job = {
  resolve: (response: Exclude<DecodeResponse, { error: string }>) => void;
  reject: (error: Error) => void;
};

export function createTileDecoder(): TileDecoder {
  const count = Math.max(2, Math.min(6, (navigator.hardwareConcurrency || 2) - 1));
  const waiting = new Map<number, Job>();
  const workers = Array.from({ length: count }, () => {
    const worker = new Worker(new URL("./tile-decoder.worker.ts", import.meta.url), {
      type: "module",
    });
    worker.onmessage = (event: MessageEvent<DecodeResponse>) => {
      const job = waiting.get(event.data.id);
      if (!job) return;
      waiting.delete(event.data.id);
      if ("error" in event.data) job.reject(new Error(event.data.error));
      else job.resolve(event.data);
    };
    return worker;
  });
  let next = 0;
  const send = (request: DecodeRequest, transfer: ArrayBuffer[]) => {
    const worker = workers[request.id % workers.length] as Worker;
    return new Promise<Exclude<DecodeResponse, { error: string }>>((resolve, reject) => {
      waiting.set(request.id, { resolve, reject });
      worker.postMessage(request, transfer);
    });
  };
  return {
    workers: count,
    async decode(type, bytes, encoding) {
      const response = await send({ id: next++, job: "pixels", type, bytes, encoding }, [bytes]);
      if (!("pixels" in response)) throw new Error("A tile came back without its pixels.");
      return type === "number" || type === "elevation"
        ? new Int32Array(response.pixels)
        : new Uint8Array(response.pixels);
    },
    async check(bytes, tileType, type) {
      const copy = bytes.slice().buffer as ArrayBuffer;
      const response = await send({ id: next++, job: "check", type, bytes: copy, tileType }, [
        copy,
      ]);
      if (!("reading" in response)) throw new Error("A tile came back unchecked.");
      return response.reading;
    },
    dispose() {
      for (const worker of workers) worker.terminate();
      for (const job of waiting.values()) job.reject(new Error("The map closed."));
      waiting.clear();
    },
  };
}
